import React, { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { mediaBreakpoint } from '@root/src/services/responsiveProvider';
import getRoute from '@root/src/services/routeList';
import { textH3 } from '@features/ui/styles/textStyles';
import { ICON_NAME } from '@features/ui/components/Icon';
import {
  selectAccessesOnMyFriends,
  selectCurrentUserDetail,
  selectIsAuthenticated,
} from '@features/users/slices/userSlice';
import { setEvents } from '@features/eventDetail/slices/eventDetailServerSlice';
import { clearFormSubscriptions, selectSubscriptionPack, setSubscriptionFor } from '@features/subscriptions/slices/formSubscriptionSlice';
import Button, { BUTTON_VARIANT } from '@features/ui/components/Button';
import { useGetEventData } from '@features/eventDetail/GetEventData';
import InputSelect from '@features/ui-input/components/InputSelect';
import { selectSerieDetail } from '../slices/serieDetailServerSlice';
import { useGetSerieData } from '../GetSerieData';

const SerieSubscriptionForSelect = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();
  const getSerieData = useGetSerieData();
  const getEventData = useGetEventData();

  const allAccesses = useSelector(selectAccessesOnMyFriends);
  const userDetail = useSelector(selectCurrentUserDetail);
  const logged = useSelector(selectIsAuthenticated);
  const serie = useSelector(selectSerieDetail);
  const pack = useSelector(selectSubscriptionPack);

  const subscriptionForOptions = logged && allAccesses.map(access => ({
    label: `${access.firstName} ${access.lastName}`,
    value: access._id,
  }));

  const [selectedProfileId, setSelectedProfileId] = useState(userDetail._id);

  const updateProfileEventsAndCategories = async (profileId) => {
    const profile = allAccesses.find(access => access._id === profileId);
    dispatch(setSubscriptionFor({ profile }));
    dispatch(clearFormSubscriptions());
    setSelectedProfileId(profileId);

    if (profile && pack) {
      // TODO: add a loading state?
      const packDetails = await getSerieData.getSeriePack(serie._idMso, pack._idMso, profile._idMso);

      const events = packDetails.reduce((acc, { event, categories }) => ({
        ...acc,
        [event._idMso]: {
          event,
          categories,
        },
      }), {});
      dispatch(setEvents({ apiEvents: events }));

      await Promise.all(packDetails.map(async ({ event }) => {
        await getEventData.getHasDiscountVoucher(event._idMso);
        await getEventData.getEventQuotas(event._idMso);
      }));
    } else {
      dispatch(setEvents({ apiEvents: {} }));
    }
  };

  const changeSubscriptionFor = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = e.target.value;
    await updateProfileEventsAndCategories(id);
  };

  useEffect(() => {
    updateProfileEventsAndCategories(selectedProfileId);
  }, [pack]);

  return logged ? (
    <Container>
      <Label>{t('serieSubscriptionForSelect_label.message')}</Label>

      <StyledSelect
        defaultValue={userDetail._id}
        options={subscriptionForOptions}
        value={selectedProfileId}
        onChange={changeSubscriptionFor}
      />

      <Button
        variant={BUTTON_VARIANT.link}
        text={t('serieSubscriptionForSelect_addFriend.message')}
        leftIcon={ICON_NAME.iconPersonAdd}
        url={getRoute.openModal(router)
          .addFriend()}
        urlScroll={false}
      />
    </Container>
  ) : <></>;
};

export default SerieSubscriptionForSelect;

const Container = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 20px 0;

  ${mediaBreakpoint.mobile} {
    flex-direction: column;

    > *:not(:first-child) {
      margin-top: 12px;
    }

    align-items: flex-start;
  }
`;

const Label = styled.h3`
  ${textH3};
  color: var(--color-dark);
  margin-right: 20px;
`;

const StyledSelect = styled(InputSelect)`
  width: 300px;
  margin-right: auto;
`;
